import { SnoozeData, SnoozeTime } from "@/protocols/snooze"

import { defaultSnoozeTime } from "@/utils/snooze"
import { saveSnooze } from "@/utils/snoozePersistence"

import { ErrorType, parseError } from "@/hooks/useValidation"

import { Notification } from "@/components"

import ErrorHandlerService from "@/services/ErrorHandler"
import SnoozeService, { CreateSnoozeRequest } from "@/services/Snooze"

import { calculateSnoozeTimeInMilliseconds } from "@/pages/Attendance/Chat/ConversationPanel/ConversationHeader/NewSnooze/utils/snoozeTime"
import ScheduleService from "@/services/Schedule"
import { useChatGlobalStateStore } from "@/store/ChatGlobalState"
import { useGlobalStateStore } from "@/store/GlobalState"

const useSnooze = (props: {
	inboxChannelChatId: number
}) => {
	const {
		inboxChannelChatId
	} = props

	const chatGlobalStateStore = useChatGlobalStateStore()
	const globalStateStore = useGlobalStateStore()

	const inboxChannelChat = chatGlobalStateStore.chat.getById(inboxChannelChatId)

	const scheduleId = inboxChannelChat.schedule?.id
	const userId = globalStateStore.user.id
	const instanceId = globalStateStore.instance.instance_id

	const handleCreateSnooze = async (input: Omit<CreateSnoozeRequest, "inboxChannelChatId"> & {
		onUpdateSnoozeTime: (snoozeTime: SnoozeTime) => void
		snoozeData: SnoozeData
	}) => {
		const {
			onUpdateSnoozeTime,
			snoozeData,
			content,
			wabaTemplateMessage,
			snoozeType
		} = input

		try {
			const snoozeTimeInMilliseconds = calculateSnoozeTimeInMilliseconds(snoozeData)

			const createdSnooze = await SnoozeService.createSnooze({
				inboxChannelChatId,
				content,
				wabaTemplateMessage,
				snoozeType
			})

			const newSchedule = await ScheduleService.scheduleSnooze({
				inboxChannelChatId,
				userId,
				snoozeId: createdSnooze.id,
				snoozeNote: snoozeData.snoozeNote,
				snoozeTimeInMilliseconds: String(snoozeTimeInMilliseconds)
			})

			await chatGlobalStateStore.chat.updateById(inboxChannelChatId, {
				status: "snooze",
				schedule: {
					id: newSchedule.id,
					time: new Date(newSchedule.time),
					snooze_id: createdSnooze.id
				}
			})

			onUpdateSnoozeTime(defaultSnoozeTime)

			saveSnooze({
				userId,
				instanceId,
				snooze: {
					snoozeTimeOption: snoozeData.snoozeTime.timeOption.type,
					value: snoozeData.snoozeTime.time,
					fullTime: snoozeData?.snoozeTime?.fullTime
				}
			})

			Notification.success({ message: "Soneca definida com sucesso." })
		} catch (error) {
			const parsedError = parseError(error as ErrorType)
			const validationMessages = parsedError?.validationMessages

			if (validationMessages) {
				if (validationMessages?.waba_template_message_id) {
					Notification.error({ message: "Não é possivel criar um soneca sem um template waba." })
				} else if (validationMessages?.content_messages) {
					Notification.error({ message: "Não é possivel criar um soneca sem uma mensagem." })
				} else {
					Notification.error({ message: "Houve um erro ao tentar definir a soneca." })
				}
			}

			ErrorHandlerService.handle(error as ErrorType)
		}
	}

	const handleCancelSnooze = async () => {
		try {
			await SnoozeService.cancelSnooze({
				scheduleId: scheduleId as number,
				userId
			})

			await chatGlobalStateStore.chat.updateById(inboxChannelChatId, {
				status: "on-going"
			})

			Notification.success({ message: "Soneca cancelada com sucesso." })
		} catch (error) {
			Notification.error({ message: "Houve um erro ao tentar cancelar o soneca." })

			ErrorHandlerService.handle(error as ErrorType)
		}
	}

	return {
		handleCreateSnooze,
		handleCancelSnooze
	}
}

export default useSnooze
